import { useQuery } from '@tanstack/react-query'
import fetch from '~/common/fetch'
import { fetchWithAlerts } from '~/common/fetchUtils'
import { flattenObject } from '~/common/objectUtils'
import { BASE_URL, handleExceptions } from './constants'

const activeVersionKeys = [
  'title',
  'planned_expense_amount',
  'planned_labor_amount',
  'planned_sow_amount',
  'externally_invoiced_amount',
  'currency_code',
  'budget_details',
  'planned_start_date',
  'planned_end_date',
  'business_unit',
  'vendor_project_manager',
  'client_project_manager',
  'description',
  'component_instance',
  'payment_type',
  'multiple_payment_type',
  'billable_work_type_profile',
  'non_billable_work_type_profile',
  'track_non_billable_workers',
  'worksites'
]
export const shapeUpdateObject = (details, versionName = '_active_version') => {
  const flattenedObject = flattenObject(details)
  const versionObject = {}
  activeVersionKeys.forEach(keyName => {
    versionObject[keyName] = flattenedObject[keyName]
    delete flattenedObject[keyName]
  })
  return { ...flattenedObject, [versionName]: versionObject }
}

const get = async (id, versionId) => {
  return fetch.get(versionId ? `${BASE_URL}${id}/version/${versionId}` : `${BASE_URL}${id}`)
}

const update = async (id, details, msg) =>
  fetchWithAlerts(
    fetch.put,
    `${BASE_URL}${id}/`,
    shapeUpdateObject(details),
    msg('common.ui.saved_successfully'),
    null,
    ['error', 'non_field_errors']
  )

const sendToVendor = async id => fetch.get(`${BASE_URL}${id}/send-sow-to-vendor/`).catch(err => handleExceptions(err))

const sendToClient = async id => fetch.get(`${BASE_URL}${id}/send-sow-to-client/`).catch(err => handleExceptions(err))

const sendToCountered = async id =>
  fetch.get(`${BASE_URL}${id}/send-sow-to-countered/`).catch(err => handleExceptions(err))

const accept = async (id, approvalsRequested, approvers) =>
  fetch
    .post(`${BASE_URL}${id}/accept-sow/`, { approvals_requested: approvalsRequested, approvers })
    .catch(err => handleExceptions(err))

const cancel = async (id, reasons, msg) =>
  fetchWithAlerts(fetch.patch, `${BASE_URL}${id}/cancel-sow/`, reasons, msg('common.ui.modified_successfully'))

const reopen = async (id, reasons, msg) =>
  fetchWithAlerts(fetch.patch, `${BASE_URL}${id}/reopen/`, reasons, msg('common.ui.modified_successfully'))

const reject = async (id, reasons, msg) =>
  fetchWithAlerts(fetch.patch, `${BASE_URL}${id}/reject-sow/`, reasons, msg('common.ui.modified_successfully'))

const getComponentInstance = async (sowId, id) =>
  fetch.get(`${BASE_URL}${sowId}/component-instance/${id}/`).catch(err => handleExceptions(err))

const updateComponentInstance = async (sowId, id, details, msg) =>
  fetchWithAlerts(
    fetch.put,
    `${BASE_URL}${sowId}/component-instance/${id}/`,
    details,
    msg('common.ui.saved_successfully')
  )

const toPDF = async id => fetch.getPdf(`${BASE_URL}${id}/download/`)

const getSowBudget = async (id, versionId) =>
  fetch.get(`${BASE_URL}${id}/version/${versionId}/get-sow-budget/`).catch(err => handleExceptions(err))

const getVendorUsers = async (sowId, vendorId) => {
  const url = vendorId ? `${BASE_URL}${sowId}/vendor-users/?id=${vendorId}` : `${BASE_URL}${sowId}/vendor-users/`
  return fetch.get(url).catch(err => handleExceptions(err))
}

const getSOWClientManagers = async sowId =>
  fetch.get(`${BASE_URL}${sowId}/participants/project-managers/`).catch(err => handleExceptions(err))

const getConfig = async (id, versionId) => fetch.get(`${BASE_URL}${id}/version/${versionId}/config/`)

const getApprovals = async id => fetch.get(`${BASE_URL}${id}/get-approvals/`)

export const sowDetailsApi = {
  get,
  getConfig,
  update,
  sendToVendor,
  sendToClient,
  sendToCountered,
  getComponentInstance,
  getVendorUsers,
  updateComponentInstance,
  accept,
  toPDF,
  cancel,
  reject,
  reopen,
  getSowBudget,
  getSOWClientManagers,
  getApprovals
}

const SOW_QUERY_KEY = 'sow'
export const useSOWQuery = id =>
  useQuery({
    queryKey: [SOW_QUERY_KEY, id],
    queryFn: () => sowDetailsApi.get(id),
    enabled: Number.isInteger(id)
  })
